import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useSnackbar } from 'notistack';
import cotizacionVentaService, {
  type CotizacionPdfImportResult,
  type CotizacionPdfPrecargaItem,
} from '../../services/cotizacionVentaService';
import CorregirArticulosGenericosPdfDialog, {
  type CorreccionArticuloGenerico,
} from './CorregirArticulosGenericosPdfDialog';
import UbicacionSelector from '../UbicacionSelector';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatNroDocumentoVenta } from '../../utils/formatters';
import {
  ensureDirectoryReadPermission,
  listarPdfsDesdeHandle,
  loadImportacionCarpetaHandle,
  loadImportacionPresupuestosPrefs,
  saveImportacionCarpetaHandle,
  saveImportacionPresupuestosPrefs,
} from '../../utils/importacionPresupuestosPrefs';

type FiltroResultado = 'todas' | 'nuevas' | 'errores' | 'existentes';

type ResultadoFila = {
  nombre: string;
  ok: boolean;
  duplicado?: boolean;
  mensaje: string;
  letra?: string | null;
  nroSuc?: number | null;
  nroComprobante?: number | null;
  nroCotizacionPdf?: string | null;
};

function comprobanteDe(fila: ResultadoFila): string {
  return formatNroDocumentoVenta(fila.letra, fila.nroSuc, fila.nroComprobante) || '—';
}

type DecisionCorreccion =
  | { accion: 'importar'; correcciones: CorreccionArticuloGenerico[] }
  | { accion: 'omitir' };

type CorreccionPendiente = {
  archivo: string;
  items: CotizacionPdfPrecargaItem[];
};

interface ImportarCarpetaCotizacionesDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  sucursales: { nroSuc: number; nombre?: string }[];
  nroSucInicial?: number;
}

async function listarPdfsDesdeCarpeta(): Promise<{
  files: File[];
  handle: FileSystemDirectoryHandle | null;
  carpetaNombre: string;
}> {
  const w = window as Window & {
    showDirectoryPicker?: () => Promise<FileSystemDirectoryHandle>;
  };

  if (typeof w.showDirectoryPicker === 'function') {
    const dir = await w.showDirectoryPicker();
    const files = await listarPdfsDesdeHandle(dir);
    return { files, handle: dir, carpetaNombre: dir.name || '' };
  }

  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'application/pdf,.pdf';
    (input as HTMLInputElement & { webkitdirectory?: boolean }).webkitdirectory = true;
    input.onchange = () => {
      const list = Array.from(input.files || []).filter((f) =>
        f.name.toLowerCase().endsWith('.pdf'),
      );
      const carpetaNombre =
        list[0]?.webkitRelativePath?.split('/')[0]?.trim() || '';
      resolve({ files: list, handle: null, carpetaNombre });
    };
    input.oncancel = () => reject(new Error('Selección cancelada'));
    input.click();
  });
}

const ImportarCarpetaCotizacionesDialog: React.FC<ImportarCarpetaCotizacionesDialogProps> = ({
  open,
  onClose,
  onDone,
  sucursales,
  nroSucInicial,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [nroSuc, setNroSuc] = useState<number>(0);
  const [ubicacion, setUbicacion] = useState('');
  const [nroUbicacion, setNroUbicacion] = useState<number | null>(null);
  const [carpetaNombre, setCarpetaNombre] = useState('');
  const [archivos, setArchivos] = useState<File[]>([]);
  const [procesando, setProcesando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [resultados, setResultados] = useState<ResultadoFila[]>([]);
  const [filtro, setFiltro] = useState<FiltroResultado>('nuevas');
  const [restaurandoCarpeta, setRestaurandoCarpeta] = useState(false);
  const cancelRef = useRef(false);
  const hidratoRef = useRef(false);
  const correccionWaiter = useRef<((decision: DecisionCorreccion) => void) | null>(null);
  const [correccion, setCorreccion] = useState<CorreccionPendiente | null>(null);

  const persistPrefs = useCallback(
    (partial: {
      nroSuc?: number;
      nroUbicacion?: number | null;
      ubicacionLabel?: string;
      carpetaNombre?: string;
    }) => {
      saveImportacionPresupuestosPrefs({
        ...(partial.nroSuc != null ? { nroSuc: partial.nroSuc } : {}),
        ...(partial.nroUbicacion != null
          ? { nroUbicacion: partial.nroUbicacion }
          : {}),
        ...(partial.ubicacionLabel != null
          ? { ubicacionLabel: partial.ubicacionLabel }
          : {}),
        ...(partial.carpetaNombre != null
          ? { carpetaNombre: partial.carpetaNombre }
          : {}),
      });
    },
    [],
  );

  useEffect(() => {
    if (!open) {
      hidratoRef.current = false;
      return;
    }
    if (hidratoRef.current) return;
    hidratoRef.current = true;

    const prefs = loadImportacionPresupuestosPrefs();
    const sucursalValida =
      prefs.nroSuc > 0 && sucursales.some((s) => s.nroSuc === prefs.nroSuc)
        ? prefs.nroSuc
        : nroSucInicial && nroSucInicial > 0
          ? nroSucInicial
          : 0;
    setNroSuc(sucursalValida);
    if (prefs.nroUbicacion > 0) {
      setNroUbicacion(prefs.nroUbicacion);
      setUbicacion(
        prefs.ubicacionLabel.trim() || `Ubicación (${prefs.nroUbicacion})`,
      );
    }
    setCarpetaNombre(prefs.carpetaNombre);
    setArchivos([]);
    setProcesando(false);
    setProgreso(0);
    setResultados([]);
    setFiltro('nuevas');
    cancelRef.current = false;

    let cancelled = false;
    (async () => {
      setRestaurandoCarpeta(true);
      try {
        const handle = await loadImportacionCarpetaHandle();
        if (!handle || cancelled) return;
        const ok = await ensureDirectoryReadPermission(handle);
        if (!ok || cancelled) return;
        const files = await listarPdfsDesdeHandle(handle);
        if (cancelled) return;
        setArchivos(files);
        const nombre = handle.name || prefs.carpetaNombre;
        if (nombre) {
          setCarpetaNombre(nombre);
          saveImportacionPresupuestosPrefs({ carpetaNombre: nombre });
        }
      } catch {
        // sin permiso o API no disponible: el usuario elige de nuevo
      } finally {
        if (!cancelled) setRestaurandoCarpeta(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, sucursales, nroSucInicial]);

  const reset = useCallback(() => {
    setArchivos([]);
    setProcesando(false);
    setProgreso(0);
    setResultados([]);
    setFiltro('nuevas');
    setRestaurandoCarpeta(false);
  }, []);

  const resolverCorreccion = (decision: DecisionCorreccion) => {
    const waiter = correccionWaiter.current;
    correccionWaiter.current = null;
    setCorreccion(null);
    waiter?.(decision);
  };

  const esperarCorreccion = (archivo: string, items: CotizacionPdfPrecargaItem[]) =>
    new Promise<DecisionCorreccion>((resolve) => {
      correccionWaiter.current = resolve;
      setCorreccion({ archivo, items });
    });

  const handleClose = () => {
    if (procesando) {
      cancelRef.current = true;
    }
    resolverCorreccion({ accion: 'omitir' });
    reset();
    onClose();
  };

  const handleElegirCarpeta = async () => {
    try {
      const { files, handle, carpetaNombre: nombre } = await listarPdfsDesdeCarpeta();
      setArchivos(files);
      setResultados([]);
      if (nombre) setCarpetaNombre(nombre);
      if (handle) {
        try {
          await saveImportacionCarpetaHandle(handle);
        } catch {
          // ignore
        }
      }
      persistPrefs({ carpetaNombre: nombre });
      if (files.length === 0) {
        enqueueSnackbar('No se encontraron PDF en la carpeta.', { variant: 'warning' });
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'No se pudo abrir la carpeta';
      if (!msg.toLowerCase().includes('cancel')) {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    }
  };

  const handleImportar = async () => {
    if (!nroSuc || nroSuc <= 0) {
      enqueueSnackbar('Seleccioná una sucursal.', { variant: 'warning' });
      return;
    }
    if (!nroUbicacion || nroUbicacion <= 0) {
      enqueueSnackbar('Seleccioná una ubicación origen.', { variant: 'warning' });
      return;
    }
    if (archivos.length === 0) {
      enqueueSnackbar('Elegí una carpeta con PDF.', { variant: 'warning' });
      return;
    }

    persistPrefs({
      nroSuc,
      nroUbicacion,
      ubicacionLabel: ubicacion,
      carpetaNombre,
    });

    setProcesando(true);
    cancelRef.current = false;
    setFiltro('nuevas');
    const filas: ResultadoFila[] = [];
    setResultados([]);

    for (let i = 0; i < archivos.length; i++) {
      if (cancelRef.current) break;
      const file = archivos[i];
      setProgreso(Math.round((i / archivos.length) * 100));
      try {
        let correcciones: { indice: number; codigoArticulo: string; descripcion: string }[] | undefined;
        const precarga = await cotizacionVentaService.precargarDesdePdf(file);
        const items = precarga.items || [];
        const genericos = items
          .map((item, index) => ({ item, index }))
          .filter((entry) => entry.item.usoArticuloGenerico);
        if (genericos.length > 0) {
          if (cancelRef.current) break;
          const decision = await esperarCorreccion(
            file.name,
            genericos.map((entry) => entry.item),
          );
          if (cancelRef.current || decision.accion === 'omitir') {
            if (!cancelRef.current) {
              filas.push({
                nombre: file.name,
                ok: false,
                mensaje: 'Omitido: artículos genéricos sin confirmar.',
              });
              setResultados([...filas]);
            }
            if (cancelRef.current) break;
            continue;
          }
          correcciones = decision.correcciones.flatMap((corr) => {
            const destino = genericos[corr.indice];
            if (!destino) return [];
            return [{
              indice: destino.index,
              codigoArticulo: corr.codigoArticulo,
              descripcion: corr.descripcion,
            }];
          });
        }
        if (cancelRef.current) break;
        const res: CotizacionPdfImportResult = await cotizacionVentaService.importarDesdePdf(file, {
          nroSuc,
          nroUbicacionOrigen: nroUbicacion,
          correcciones,
        });
        filas.push({
          nombre: file.name,
          ok: !res.duplicado && Boolean(res.nroComprobante || res.nroFormulario),
          duplicado: Boolean(res.duplicado),
          mensaje: res.mensaje || (res.duplicado ? 'Ya existía' : 'Creada'),
          letra: res.letra,
          nroSuc: res.nroSuc,
          nroComprobante: res.nroComprobante,
          nroCotizacionPdf: res.precarga?.formulario?.ordenDeCompra,
        });
      } catch (error: unknown) {
        const err = error as {
          response?: { status?: number; data?: CotizacionPdfImportResult & { message?: string } };
        };
        const data = err.response?.data;
        filas.push({
          nombre: file.name,
          ok: false,
          duplicado: Boolean(data?.duplicado) || err.response?.status === 409,
          mensaje: data?.mensaje || data?.message || getApiErrorMessage(error) || 'Error',
          letra: data?.letra,
          nroSuc: data?.nroSuc,
          nroComprobante: data?.nroComprobante,
          nroCotizacionPdf: data?.precarga?.formulario?.ordenDeCompra,
        });
      }
      setResultados([...filas]);
    }

    setProgreso(100);
    setProcesando(false);
    const okCount = filas.filter((f) => f.ok).length;
    const dupCount = filas.filter((f) => f.duplicado).length;
    if (okCount > 0) onDone();
    if (cancelRef.current) return;
    enqueueSnackbar(
      `Importación finalizada: ${okCount} ok, ${dupCount} duplicados, ${filas.length - okCount - dupCount} errores.`,
      { variant: okCount > 0 ? 'success' : 'warning' },
    );
  };

  const etiquetaCarpeta =
    archivos.length === 0
      ? carpetaNombre
        ? `Carpeta recordada: ${carpetaNombre}${restaurandoCarpeta ? ' (restaurando…)' : ' — elegí de nuevo si no cargó'}`
        : restaurandoCarpeta
          ? 'Restaurando carpeta…'
          : 'Sin PDF seleccionados'
      : `${archivos.length} PDF${carpetaNombre ? ` — ${carpetaNombre}` : ''}`;

  return (
    <>
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (correccion && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Importar presupuestos desde carpeta</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info" variant="outlined">
            Elegí una carpeta local (o sincronizada en tu PC). Cada PDF se importa como cotización.
            Si un renglón no coincide con un artículo del maestro, se abre una ventana para corregirlo antes de crear esa cotización.
            Sucursal, ubicación y carpeta se recuerdan en este navegador.
            Requiere módulo IMPORTACION_PRESUPUESTOS y artículo genérico configurado.
          </Alert>

          <TextField
            select
            label="Sucursal"
            value={nroSuc || ''}
            onChange={(e) => {
              const next = Number(e.target.value) || 0;
              setNroSuc(next);
              persistPrefs({ nroSuc: next });
            }}
            fullWidth
            disabled={procesando}
            size="small"
          >
            {sucursales.map((s) => (
              <MenuItem key={s.nroSuc} value={s.nroSuc}>
                {s.nombre ? `${s.nroSuc} — ${s.nombre}` : String(s.nroSuc)}
              </MenuItem>
            ))}
          </TextField>

          <UbicacionSelector
            label="Ubicación origen"
            value={ubicacion}
            onChange={(v, nro) => {
              setUbicacion(v);
              setNroUbicacion(nro ?? null);
              persistPrefs({
                ubicacionLabel: v,
                nroUbicacion: nro ?? 0,
              });
            }}
            disabled={procesando}
            autoSelectPreferida={!ubicacion}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={
                restaurandoCarpeta ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <FolderOpenIcon />
                )
              }
              onClick={() => void handleElegirCarpeta()}
              disabled={procesando || restaurandoCarpeta}
            >
              {carpetaNombre ? 'Cambiar carpeta' : 'Elegir carpeta'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {etiquetaCarpeta}
            </Typography>
          </Stack>

          {procesando && (
            <Box>
              <LinearProgress variant="determinate" value={progreso} />
              <Typography variant="caption" color="text.secondary">
                Procesando… {progreso}%
              </Typography>
            </Box>
          )}

          {resultados.length > 0 && (
            <>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {([
                  ['todas', 'Todas', resultados.length],
                  ['nuevas', 'Nuevas', resultados.filter((r) => r.ok).length],
                  ['errores', 'Con error', resultados.filter((r) => !r.ok && !r.duplicado).length],
                  ['existentes', 'Ya existían', resultados.filter((r) => r.duplicado).length],
                ] as const).map(([id, label, count]) => (
                  <Chip
                    key={id}
                    label={`${label} (${count})`}
                    size="small"
                    color={id === 'errores' ? 'error' : id === 'existentes' ? 'warning' : id === 'nuevas' ? 'success' : 'default'}
                    variant={filtro === id ? 'filled' : 'outlined'}
                    onClick={() => setFiltro(id)}
                  />
                ))}
              </Stack>
              <TableContainer sx={{ maxHeight: 280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Archivo</TableCell>
                      <TableCell>Nro del PDF</TableCell>
                      <TableCell>Resultado</TableCell>
                      <TableCell>Comprobante</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const visibles = resultados.filter((r) => {
                        if (filtro === 'nuevas') return r.ok;
                        if (filtro === 'errores') return !r.ok && !r.duplicado;
                        if (filtro === 'existentes') return Boolean(r.duplicado);
                        return true;
                      });
                      if (visibles.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={4}>
                              <Typography variant="body2" color="text.secondary">
                                No hay archivos en este filtro.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return visibles.map((r) => (
                        <TableRow key={r.nombre}>
                          <TableCell>{r.nombre}</TableCell>
                          <TableCell>{r.nroCotizacionPdf?.trim() || '—'}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={r.ok ? 'success.main' : r.duplicado ? 'warning.main' : 'error.main'}
                            >
                              {r.mensaje}
                            </Typography>
                          </TableCell>
                          <TableCell>{comprobanteDe(r)}</TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{procesando ? 'Cancelar' : 'Cerrar'}</Button>
        <Button
          variant="contained"
          onClick={() => void handleImportar()}
          disabled={procesando || archivos.length === 0}
          startIcon={procesando ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Importar
        </Button>
      </DialogActions>
    </Dialog>
    <CorregirArticulosGenericosPdfDialog
      open={correccion != null}
      items={correccion?.items || []}
      archivo={correccion?.archivo}
      textoCerrar="Omitir este PDF"
      onClose={() => resolverCorreccion({ accion: 'omitir' })}
      onConfirm={(elegidas) => resolverCorreccion({ accion: 'importar', correcciones: elegidas })}
    />
    </>
  );
};

export default ImportarCarpetaCotizacionesDialog;
